import React from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import heroImage from "../images/packaging.png";

const ticker = [
  "BOPP Packaging Films",
  "Polyolefin Shrink Films",
  "LD Poly Bags",
  "High Molecular Bags",
  "Custom Printed Packaging",
  "Pan-India Delivery",
  "Manufacturer & Trader",
];

const Section1 = () => (
  <HeroSection>
    <HeroInner>
      {/* Left — text */}
      <HeroLeft>
        <Overline>Poly Bag Solutions &nbsp;·&nbsp; Manufacturer &amp; Trader &nbsp;·&nbsp; India</Overline>

        <HeadlineBlock>
          <LineLight>India's Trusted</LineLight>
          <LineBold>Poly Bag</LineBold>
          <LineAccent>Manufacturer and Trader.</LineAccent>
        </HeadlineBlock>

        <Rule />

        <Subtitle>
          BOPP, POF, LD &amp; HM poly bags with custom printing.
          Pan-India delivery.
        </Subtitle>

        <Actions>
          <Link to="/products"><BtnDark>Explore Products</BtnDark></Link>
          <Link to="/contact"><BtnOutline>Request a Quote</BtnOutline></Link>
        </Actions>

        <MetaRow>
          <MetaItem>
            <MetaNum>4+</MetaNum>
            <MetaLabel>Product Lines</MetaLabel>
          </MetaItem>
          <MetaDivider />
          <MetaItem>
            <MetaNum>Custom</MetaNum>
            <MetaLabel>Printing</MetaLabel>
          </MetaItem>
          <MetaDivider />
          <MetaItem>
            <MetaNum>Pan-India</MetaNum>
            <MetaLabel>Delivery</MetaLabel>
          </MetaItem>
        </MetaRow>
      </HeroLeft>

      {/* Right — image */}
      <HeroRight>
        <ImageWrap>
          <img src={heroImage} alt="BR Plast plastic packaging manufacturing" />
        </ImageWrap>
      </HeroRight>
          <ImageTag>pan-india delivery</ImageTag>
    </HeroInner>

    {/* Marquee ticker */}
    <TickerBand>
      <TickerTrack>
        {[...ticker, ...ticker].map((item, i) => (
          <TickerItem key={i}>
            {item}<TickerDot>·</TickerDot>
          </TickerItem>
        ))}
      </TickerTrack>
    </TickerBand>
  </HeroSection>
);

const ImageTag = styled.div`
  position: absolute;
  bottom: 11%;
  right: 16px;
  background: var(--color-accent);
  color: white;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 8px 18px;
  border-radius: 50px;
  box-shadow: 0 4px 12px rgba(224,135,36,0.3);
  text-transform: uppercase;
`;

/* ─── Animations ─────────────────────────────────────── */
const tickerScroll = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

/* ─── Styled Components ──────────────────────────────── */
const HeroSection = styled.section`
  min-height: 100vh;
  padding-top: var(--navbar-h);
  background: var(--color-bg);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`;

const HeroInner = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const HeroLeft = styled.div`
  flex: 0 0 54%;
  padding: 60px 64px 48px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 900px) {
    flex: 1;
    padding: 40px 24px 32px;
    width: 100%;
  }
`;

const Overline = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: var(--color-muted);
  margin-bottom: 28px;
`;

const HeadlineBlock = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0;
`;

const LineLight = styled.span`
  font-size: 44px;
  font-weight: 300;
  color: #b0b0b0;
  letter-spacing: -0.5px;
  line-height: 1.1;
  font-family: var(--font-primary);

  @media (max-width: 768px) { font-size: 26px; }
`;

const LineBold = styled.span`
  font-size: 96px;
  font-weight: 900;
  color: var(--color-dark);
  letter-spacing: -4px;
  line-height: 0.9;
  font-family: var(--font-primary);

  @media (max-width: 768px) { font-size: 56px; letter-spacing: -2px; }
`;

const LineAccent = styled.span`
  font-size: 60px;
  font-weight: 700;
  color: var(--color-brand);
  letter-spacing: -1.5px;
  line-height: 1.1;
  font-family: var(--font-primary);

  @media (max-width: 768px) { font-size: 36px; letter-spacing: -0.5px; }
`;

const Rule = styled.hr`
  border: none;
  border-top: 1px solid var(--color-border);
  margin: 32px 0;
  width: 64px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: var(--color-muted);
  line-height: 1.75;
  max-width: 380px;
  margin: 0 0 36px;

  @media (max-width: 768px) { font-size: 15px; }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 48px;

  a { text-decoration: none; }
`;

const BtnDark = styled.button`
  background: var(--color-dark);
  color: white;
  border: none;
  padding: 14px 32px;
  border-radius: var(--radius-btn);
  font-size: 14px;
  font-weight: 600;
  font-family: var(--font-primary);
  cursor: pointer;
  transition: var(--transition);
  letter-spacing: 0.5px;

  &:hover {
    background: var(--color-brand);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(66,87,216,0.25);
  }
`;

const BtnOutline = styled.button`
  background: transparent;
  color: var(--color-dark);
  border: 1.5px solid var(--color-dark);
  padding: 13px 32px;
  border-radius: var(--radius-btn);
  font-size: 14px;
  font-weight: 500;
  font-family: var(--font-primary);
  cursor: pointer;
  transition: var(--transition);
  letter-spacing: 0.3px;

  &:hover {
    border-color: var(--color-brand);
    color: var(--color-brand);
    transform: translateY(-2px);
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  border-top: 1px solid var(--color-border);
  padding-top: 24px;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 28px;
`;

const MetaNum = styled.span`
  font-size: 22px;
  font-weight: 800;
  color: var(--color-dark);
  line-height: 1;
`;

const MetaLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: var(--color-muted);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 4px;
`;

const MetaDivider = styled.div`
  width: 1px;
  height: 32px;
  background: var(--color-border);
  margin: 0 28px 0 0;
  flex-shrink: 0;
`;

/* Right image panel */
const HeroRight = styled.div`
  flex: 0 0 46%;
  height: calc(100vh - var(--navbar-h));
  overflow: hidden;
  background: var(--color-bg-subtle);
  border-left: 1px solid var(--color-border);

  @media (max-width: 900px) {
    display: none;
  }
`;

const ImageWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;


/* Marquee */
const TickerBand = styled.div`
  background: var(--color-dark);
  overflow: hidden;
  padding: 13px 0;
  border-top: 1px solid rgba(255,255,255,0.06);
`;

const TickerTrack = styled.div`
  display: flex;
  width: max-content;
  animation: ${tickerScroll} 35s linear infinite;

  &:hover { animation-play-state: paused; }
`;

const TickerItem = styled.span`
  white-space: nowrap;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255,255,255,0.6);
  padding: 0 6px;
  letter-spacing: 2px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
`;

const TickerDot = styled.span`
  color: var(--color-accent);
  font-size: 18px;
  margin: 0 20px;
  line-height: 1;
`;

export default Section1;
